class CacheMetrics {
  constructor() {
    this.metrics = {
      hits: { memory: 0, indexeddb: 0, deduplication: 0, stale: 0 },
      misses: { fetch_required: 0, expired: 0, not_found: 0, error: 0 },
      apiCalls: { total: 0, cached: 0, fresh: 0 },
      performance: []
    };

    this.sessionStart = Date.now();
    this.initializePersistedMetrics();
  }

  initializePersistedMetrics() {
    const stored = localStorage.getItem('cacheMetrics');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.date === new Date().toDateString()) {
          this.metrics = { ...this.metrics, ...parsed.metrics };
        }
      } catch (e) {
        console.error('Failed to parse stored metrics:', e);
      }
    }
  }

  persistMetrics() {
    localStorage.setItem('cacheMetrics', JSON.stringify({
      date: new Date().toDateString(),
      metrics: this.metrics
    }));
  }

  trackCacheHit(key, source) {
    this.metrics.hits[source] = (this.metrics.hits[source] || 0) + 1;
    this.logEvent('cache_hit', { key, source });
    this.persistMetrics();
  }

  trackCacheMiss(key, reason) {
    this.metrics.misses[reason] = (this.metrics.misses[reason] || 0) + 1;
    this.logEvent('cache_miss', { key, reason });
    this.persistMetrics();
  }

  trackApiRequest(endpoint, duration, cached) {
    this.metrics.apiCalls.total++;
    if (cached) {
      this.metrics.apiCalls.cached++;
    } else {
      this.metrics.apiCalls.fresh++;
    }

    this.metrics.performance.push({
      endpoint,
      duration,
      cached,
      timestamp: Date.now()
    });

    if (this.metrics.performance.length > 100) {
      this.metrics.performance.shift();
    }

    this.logEvent('api_request', { endpoint, duration, cached });
    this.persistMetrics();
  }

  logEvent(eventName, data) {
    if (window.gtag) {
      window.gtag('event', eventName, {
        event_category: 'cache_performance',
        ...data
      });
    }

    if (process.env.NODE_ENV === 'development') {
      console.log(`[CacheMetrics] ${eventName}:`, data);
    }
  }

  getCacheHitRate() {
    const totalHits = Object.values(this.metrics.hits).reduce((a, b) => a + b, 0);
    const totalMisses = Object.values(this.metrics.misses).reduce((a, b) => a + b, 0);
    const total = totalHits + totalMisses;

    if (total === 0) return 0;
    return ((totalHits / total) * 100).toFixed(2);
  }

  getApiReductionRate() {
    const { cached, fresh } = this.metrics.apiCalls;
    const total = cached + fresh;

    if (total === 0) return 0;
    return ((cached / total) * 100).toFixed(2);
  }

  getAverageResponseTime() {
    if (this.metrics.performance.length === 0) return 0;

    const sum = this.metrics.performance.reduce((acc, p) => acc + p.duration, 0);
    return Math.round(sum / this.metrics.performance.length);
  }

  getMetricsSummary() {
    const sessionDuration = (Date.now() - this.sessionStart) / 1000 / 60;
    const requestsPerHour = (this.metrics.apiCalls.fresh / sessionDuration) * 60;

    return {
      cacheHitRate: this.getCacheHitRate() + '%',
      apiReductionRate: this.getApiReductionRate() + '%',
      averageResponseTime: this.getAverageResponseTime() + 'ms',
      sessionDuration: sessionDuration.toFixed(1) + ' minutes',
      requestsPerHour: requestsPerHour.toFixed(0),
      totalApiCalls: this.metrics.apiCalls.total,
      cachedResponses: this.metrics.apiCalls.cached,
      freshFetches: this.metrics.apiCalls.fresh,
      hitsBySource: this.metrics.hits,
      missByReason: this.metrics.misses
    };
  }

  logSummaryToConsole() {
    const summary = this.getMetricsSummary();
    console.group('📊 Cache Performance Metrics');
    console.log('Cache Hit Rate:', summary.cacheHitRate);
    console.log('API Reduction:', summary.apiReductionRate);
    console.log('Avg Response Time:', summary.averageResponseTime);
    console.log('Session Duration:', summary.sessionDuration);
    console.log('Requests/Hour:', summary.requestsPerHour);
    console.table({
      'Total API Calls': summary.totalApiCalls,
      'Cached Responses': summary.cachedResponses,
      'Fresh Fetches': summary.freshFetches
    });
    console.log('Cache Hits by Source:', summary.hitsBySource);
    console.log('Cache Misses by Reason:', summary.missByReason);
    console.groupEnd();
  }

  resetMetrics() {
    this.metrics = {
      hits: { memory: 0, indexeddb: 0, deduplication: 0, stale: 0 },
      misses: { fetch_required: 0, expired: 0, not_found: 0, error: 0 },
      apiCalls: { total: 0, cached: 0, fresh: 0 },
      performance: []
    };
    this.sessionStart = Date.now();
    localStorage.removeItem('cacheMetrics');
  }
}

export default new CacheMetrics();